const CROP_SUITABILITY_MAP = [
  { threshold: 86, category: 'Tropical', crops: ['Citrus', 'Sugarcane', 'Avocados'] },
  { threshold: 66, category: 'Sub-Tropical', crops: ['Cotton', 'Melons', 'Peppers'] },
  { threshold: 46, category: 'Warm Temperate', crops: ['Corn', 'Tomatoes', 'Soybeans'] },
  { threshold: 31, category: 'Cool Temperate', crops: ['Wheat', 'Potatoes', 'Carrots'] },
  { threshold: 16, category: 'Cold Temperate', crops: ['Kale', 'Spinach', 'Barley'] },
  { threshold: 0,  category: 'Arctic', crops: ['Hardy Grasses'] }
]

// Helper to get recommendation from the growthPotential score
export const getCropRecommendations = (potentialScore) => {
  return CROP_SUITABILITY_MAP.find(zone => potentialScore >= zone.threshold)
}

/**
 * Calculate the true USDA Hardiness Zone based on the 
 * average annual extreme minimum temperature (Celsius).
 * Reference: USDA Plant Hardiness Zone Map scales.
 */
export const calculateUSDAZone = (avgMinTemp) => {
  if (avgMinTemp === null || avgMinTemp === undefined) return null

  // Values are Celsius thresholds for the start of each zone
  if (avgMinTemp <= -45.6) return 1
  if (avgMinTemp <= -40.0) return 2
  if (avgMinTemp <= -34.4) return 3
  if (avgMinTemp <= -28.9) return 4
  if (avgMinTemp <= -23.3) return 5
  if (avgMinTemp <= -17.8) return 6
  if (avgMinTemp <= -12.2) return 7
  if (avgMinTemp <= -6.7)  return 8
  if (avgMinTemp <= -1.1)  return 9
  if (avgMinTemp <= 4.4)   return 10
  if (avgMinTemp <= 10.0)  return 11
  if (avgMinTemp <= 15.6)  return 12
  return 13 // Tropical/Equatorial extremes
}

/**
 * Calculate USDA hardiness zone from GDD data
 * USDA zones are based on average annual minimum winter temperature
 * This approximation uses GDD10 as a proxy for winter hardiness
 */
export const estimateUSDAZoneFromGDD10 = (avg_gdd10) => {
  if (avg_gdd10 < 800)  return 3   // Very cold climates
  if (avg_gdd10 < 1200) return 4   // Cold climates
  if (avg_gdd10 < 1700) return 5   // Cool climates
  if (avg_gdd10 < 2200) return 6   // Moderate climates
  if (avg_gdd10 < 2700) return 7   // Warm temperate
  if (avg_gdd10 < 3200) return 8   // Warm climates
  if (avg_gdd10 < 3700) return 9   // Hot climates
  if (avg_gdd10 < 4200) return 10  // Very hot climates
  return 11  // Tropical climates
}

/**
 * Utility to calculate agricultural zones based on global_climate_atlas data
 */
export const calculateAgriZone = (hex) => {
  if (!hex) return null

  const { avg_gdd10, abs_min_temp, avg_ffw } = hex

  // 1. Thermal Class (Keep existing GDD logic)
  let thermalClass = 'Unknown'
  if (avg_gdd10 < 500)  thermalClass = 'Polar/Alpine'
  if (avg_gdd10 >= 500)  thermalClass = 'Cool'
  if (avg_gdd10 >= 1200) thermalClass = 'Temperate'
  if (avg_gdd10 >= 2000) thermalClass = 'Warm'
  if (avg_gdd10 >= 3500) thermalClass = 'Tropical/Arid'

  // 2. Window Class
  let windowClass = 'Critical'
  if (avg_ffw > 90)  windowClass = 'Short'
  if (avg_ffw > 140) windowClass = 'Standard'
  if (avg_ffw > 200) windowClass = 'Long'
  if (avg_ffw > 300) windowClass = 'Perennial'

  // 3. Simplified "Growth Index" (0-100)
  const growthPotential = Math.min(Math.round((avg_gdd10 / 4000) * 100), 100)

  // 4. NEW: True USDA Hardiness Zone from actual min temp
  // Fallback to the old GDD proxy only if temperature data is missing
  const usdaZone = abs_min_temp !== undefined 
    ? calculateUSDAZone(abs_min_temp) 
    : estimateUSDAZoneFromGDD10(avg_gdd10)

  return {
    thermalClass,
    windowClass,
    frostFreeDays: avg_ffw,
    growthPotential,
    usdaZone,
    label: `${thermalClass} (${windowClass} Season)`,
    color: getZoneColor(avg_gdd10)
  }
}

const getZoneColor = (gdd) => {
  if (gdd < 800) return '#4A90E2' // Cool Blue
  if (gdd < 1500) return '#7ED321' // Green
  if (gdd < 2500) return '#F5A623' // Orange
  return '#D0021B' // Red/Tropical
}
